/**
 * Background polling hook for npub.cash token claiming.
 *
 * Periodically checks npub.cash for unclaimed Cashu tokens sent to the
 * user's Lightning address. Redeems any found tokens via the provided
 * receive callback. Runs at app level (App.tsx) so tokens are claimed
 * regardless of which screen the user is on.
 *
 * Tier: 1 (beginner) — automatic background infrastructure.
 *
 * @module hooks/useNpubCashBackgroundClaim
 */

import { useEffect, useRef, useCallback } from 'react';
import { claimNpubCashTokens } from '@/lib/npub-cash';
import { useNostrIdentity } from '@/hooks/useNostrIdentity';

/** Background poll interval: 30 seconds. */
const BACKGROUND_POLL_INTERVAL_MS = 30_000;

/**
 * Background npub.cash token claimer.
 *
 * Polls every 30 seconds for unclaimed tokens and redeems them automatically.
 * Only active when the Nostr identity is available (wallet is unlocked).
 *
 * @param receiveToken - Callback to redeem a Cashu token string
 */
export function useNpubCashBackgroundClaim(
  receiveToken: (token: string) => Promise<unknown>,
): void {
  const nostrIdentity = useNostrIdentity();
  const inFlightRef = useRef(false);
  const receiveRef = useRef(receiveToken);
  receiveRef.current = receiveToken;

  const poll = useCallback(async (): Promise<void> => {
    if (inFlightRef.current || !nostrIdentity?.privkey) return;
    inFlightRef.current = true;

    try {
      const tokens = await claimNpubCashTokens(nostrIdentity.privkey);
      for (const token of tokens) {
        try {
          await receiveRef.current(token);
        } catch {
          // Individual token redeem failure — continue with others
        }
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [nostrIdentity?.privkey]);

  useEffect(() => {
    if (!nostrIdentity?.privkey) return;

    // Poll immediately on mount, then every 30 seconds
    void poll();
    const interval = setInterval(() => void poll(), BACKGROUND_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [nostrIdentity?.privkey, poll]);
}
